import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import Button from '../components/common/Button'
import ListRow from '../components/common/ListRow'
import { initialPlan, planLabel, plansWithPrices, refusalFor, trialLine, untilDate } from '../lib/subscriptions'
import { appVersionLine } from '../lib/version'
import { purchaseSubscription, startTrial, subscriptionPlans, subscriptionStatus } from '../services/subscriptions'

function CloseGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}

function CheckGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-good" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

function PlanCard({ plan, selected, onSelect, perMonth }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`flex w-full items-center gap-3 rounded-[16px] border-[1.5px] p-3.5 text-start transition ${
        selected ? 'border-brand-600 bg-brand-50 dark:bg-brand-950/40' : 'border-line bg-surface'
      }`}
    >
      <span className={`grid size-5 shrink-0 place-items-center rounded-full border-2 ${selected ? 'border-brand-600' : 'border-line'}`}>
        {selected && <span className="size-2.5 rounded-full bg-brand-600" />}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-bold text-ink">{plan.name}</span>
        <span className="text-[11px] text-ink-2">{plan.note}</span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-0.5">
        <span className="text-sm font-bold text-ink">{plan.price}</span>
        <span className="text-[11px] text-ink-2">{perMonth}</span>
      </span>
    </button>
  )
}

/**
 * The seven-day trial card (tooti#121): a start button while this phone
 * number has never had one, the days left while it runs, and a plain
 * sentence once it is over — louder when the access gate is holding the
 * learner out.
 */
function TrialCard({ status, trialDays, busy, onStart }) {
  const { t } = useTranslation('paywall')
  const state = status?.trial?.state ?? 'available'
  const tone = status?.locked ? 'border-bad bg-bad-tint' : state === 'active' ? 'border-good bg-good-tint' : 'border-line bg-surface-2'
  return (
    <section className={`space-y-2.5 rounded-[16px] border-[1.5px] p-3.5 ${tone}`}>
      <h2 className="text-sm font-bold text-ink">{t('trial.title')}</h2>
      <p className="text-[12.5px] leading-[1.9] text-ink-2">{trialLine(status, trialDays)}</p>
      {state === 'available' && status && !status.active && (
        <Button className="w-full" variant="secondary" loading={busy} onClick={onStart}>
          {t('trial.start')}
        </Button>
      )}
    </section>
  )
}

/**
 * Tooti+ (design page 19, `paywall`). Since tooti#121 the page sells the
 * client's two monthly plans — standard and premium — at the server's
 * prices, and offers the seven-day free trial, once per phone number.
 * Buying is gated server-side by SUBSCRIPTIONS_ENABLED until a real bank
 * gateway is connected; a refusal says so and points at the trial. The
 * Android app opens this page in the browser (tooti#119), optionally with
 * `?plan=`.
 */
export default function Paywall() {
  const { t } = useTranslation('paywall')
  const location = useLocation()
  const [plan, setPlan] = useState(() => initialPlan(location.search))
  const [serverPlans, setServerPlans] = useState(null)
  const [status, setStatus] = useState(null)
  const [busy, setBusy] = useState(null)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    let cancelled = false
    subscriptionPlans()
      .then((data) => {
        if (!cancelled) setServerPlans(data)
      })
      .catch(() => {})
    subscriptionStatus()
      .then((data) => {
        if (!cancelled) setStatus(data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const plans = plansWithPrices(serverPlans?.plans)
  const selected = plans.find((p) => p.key === plan) ?? plans[plans.length - 1]

  async function run(kind, action, success) {
    if (busy) return
    setBusy(kind)
    setMessage(null)
    try {
      setStatus(await action())
      setMessage({ good: true, text: success })
    } catch (e) {
      setMessage(refusalFor(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-4" aria-busy={busy ? true : undefined}>
      <header className="flex items-center justify-end">
        <Link to="/profile" aria-label={t('close')} className="grid size-10 place-items-center rounded-full text-ink-2 hover:bg-surface-2">
          <CloseGlyph />
        </Link>
      </header>

      <div className="space-y-4 text-center">
        <span className="inline-block rounded-full bg-accent px-3.5 py-1 text-xs font-bold text-on-accent">{t('brandBadge')}</span>
        <h1 className="text-xl font-bold text-ink">{t('title')}</h1>
        <p className="text-[13px] leading-[1.9] text-ink-2">{t('intro')}</p>
      </div>

      {status?.active ? (
        <section className="space-y-1.5 rounded-[16px] border-[1.5px] border-good bg-good-tint p-3.5">
          <h2 className="text-sm font-bold text-ink">{t('activeTitle')}</h2>
          <p className="text-[12.5px] leading-[1.9] text-ink-2">
            {t('activeUntil', { plan: planLabel(status), date: untilDate(status.currentPeriodEnd) })}
          </p>
        </section>
      ) : (
        <TrialCard
          status={status}
          trialDays={serverPlans?.trialDays ?? 7}
          busy={busy === 'trial'}
          onStart={() => run('trial', startTrial, t('trial.started'))}
        />
      )}

      <div className="space-y-2">
        {plans.map((p) => (
          <PlanCard key={p.key} plan={p} selected={p.key === selected.key} onSelect={() => setPlan(p.key)} perMonth={t('perMonth')} />
        ))}
      </div>

      <ul className="space-y-2.5">
        {selected.perks.map((perk) => (
          <li key={perk} className="flex items-center gap-2.5 text-[13px] text-ink">
            <CheckGlyph />
            {perk}
          </li>
        ))}
      </ul>

      <Button
        className="w-full"
        loading={busy === 'purchase'}
        onClick={() => run('purchase', () => purchaseSubscription(selected.key), t('successMessage'))}
      >
        {t('buyButton', { planName: selected.name })}
      </Button>

      <ListRow label={t('autopayRow')} to="/autopay" />

      {message && (
        <p role="status" className={`rounded-[14px] px-3.5 py-3 text-xs font-semibold leading-[1.85] ${message.good ? 'bg-good-tint text-good-ink' : 'bg-bad-tint text-bad-ink'}`}>
          {message.text}
        </p>
      )}

      <p className="text-center text-[11px] leading-[1.8] text-ink-3">{t('disclaimer')}</p>
      <p className="pt-0.5 text-center text-[11px] text-ink-3">{appVersionLine()}</p>
    </div>
  )
}
